import { useMemo } from 'react';
import { get } from 'lodash';
import instance from '@api/axiosInstance';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import getMetricsData from '@api/metricContainer';
import { formatTimeseriesData } from '@src/components/metrics-panel-hz/utils/format';
import { createQuery } from '@src/components/widget-panel/utils/query';
import useQueryParams from '../../hooks/useQueryParams';
import useWidgetQuery from '../../hooks/useWidgetQuery';
import { triggerDeletedFieldsError } from '@utils/errors/triggerPageError';

function useFetchMetrics({ wid, metrics = [], eventName = null, filter }) {
  const { interval, _rKey, allowedMetrics, liveMode } = useQueryParams();

  const allowedMetricList = useMemo(() => {
    let queryMetrics: any = [...metrics];
    queryMetrics.forEach((m) => {
      const invertedMetric = getPercentageMetricByKey({ metricKey: m, defaultValue: null });
      if (invertedMetric) {
        queryMetrics.push(invertedMetric);
      }
    });
    queryMetrics = [...new Set(queryMetrics)].sort();
    return queryMetrics;
  }, [metrics, allowedMetrics]);

  const options = {};

  const payload = createQuery(
    allowedMetricList,
    `${interval.from}/${interval.to}`,
    interval.granularity,
    filter.rule,
    options,
  );

  return useWidgetQuery({
    wid,
    queryKey: ['widget', 'metric-group', payload.queries, _rKey],
    queryFn: () =>
      getMetricsData(payload, {}, instance).then((response) => {
        const { data, totals, annotations } = get(response, ['data', '0'], {
          totals: {},
          data: [],
          annotations: [],
        });
        triggerDeletedFieldsError(annotations);
        const formattedData = formatTimeseriesData({
          metrics: allowedMetricList,
          data,
          interval,
          annotations,
        });

        return { data: formattedData, totals };
      }),
    options: {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !!metrics.length && !liveMode && !!eventName,
      staleTime: 5 * 60 * 1000, // 5m
    },
  });
}

export default useFetchMetrics;
